/* ============================================
   PRODUCT.JS — Unselected variants + inline picker error + ATC guard
   Works for products WITH and WITHOUT variants
   ============================================ */

if (!customElements.get('variant-selects')) {

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.addEventListener('change', this.onVariantChange);
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((el) => el !== this);

      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider = this.productWrapper.querySelector(`#MediaGallery-${this.dataset.section}`);
        this.hideVariants = this.productSlider?.dataset.hideVariants === 'true';
      }
    }

    connectedCallback() {
      const hasOptions = !!this.querySelector('select, input[type="radio"]');

      if (hasOptions) {
        // Force “nothing selected” for products WITH options
        this.initUnselectedState();
      } else {
        // Products WITHOUT options but component exists (edge-case themes)
        this.setDefaultVariantIdForNoOptions();
      }

      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
    }

    /* ---------- Inline error on the variant picker ---------- */
    showInlineError(message) {
      let box = this.querySelector('.variant-error');
      if (!box) {
        box = document.createElement('div');
        box.className = 'variant-error';
        box.setAttribute('role', 'alert');
        box.setAttribute('aria-live', 'polite');
        this.prepend(box);
      }
      box.textContent = message || 'Vælg venligst en størrelse.';
      box.hidden = false;
    }
    clearInlineError() {
      const box = this.querySelector('.variant-error');
      if (box) box.hidden = true;
    }
    /* -------------------------------------------------------- */

    initUnselectedState() {
      // Radios → unchecked
      this.querySelectorAll('input[type="radio"]').forEach(r => (r.checked = false));

      // Selects → insert disabled placeholder and select it
      this.querySelectorAll('select').forEach((sel) => {
        const hasPlaceholder = [...sel.options].some(o => o.value === '');
        if (!hasPlaceholder) {
          const opt = document.createElement('option');
          opt.value = '';
          opt.textContent = sel.dataset.placeholder || 'Vælg venligst en størrelse.';
          opt.disabled = true;
          opt.selected = true;
          opt.hidden = true;
          sel.insertBefore(opt, sel.firstChild);
        }
        sel.value = ''; // keep unselected
      });

      // Hidden id must be empty + disabled until a valid variant exists
      const form = this.closest('form');
      const hiddenId = form?.querySelector('input[name="id"]');
      if (hiddenId) {
        hiddenId.value = '';
        hiddenId.setAttribute('disabled', 'disabled');
      }
    }

    // For themes that render this element even when the product has no options
    setDefaultVariantIdForNoOptions() {
      const form = this.closest('form');
      const hiddenId = form?.querySelector('input[name="id"]');
      if (!hiddenId) return;

      const data = this.getVariantData?.();
      const variant = (data && (data.find(v => v.available) || data[0])) || null;

      if (variant?.id) {
        hiddenId.value = variant.id;
        hiddenId.removeAttribute('disabled');
      }
    }

    onVariantChange = () => {
      // Clear inline error as soon as user interacts
      this.clearInlineError();

      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        if (this.updateUrl) this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();
      }

      this.updateOther();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });
    };

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.option_keys = [];
      this.fieldsets.forEach((fieldset) => {
        const sel = fieldset.querySelector('select');
        const radios = fieldset.querySelectorAll('input');
        if (sel) {
          const val = sel.value;                // '' when placeholder selected
          this.options.push(val || null);       // null means "not chosen"
          this.option_keys.push(sel.name);
        } else if (radios.length) {
          const checked = fieldset.querySelector('input:checked');
          this.options.push(checked ? checked.value : null);
          this.option_keys.push(radios[0].name);
        }
      });
      this.dataset.options = JSON.stringify(this.options);
    }

    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        const label = item.querySelector('.form__label__value');
        if (label) label.textContent = this.options[i] || '';
      });
    }

    updateMasterId() {
      // Any unchosen option → no currentVariant
      if (!this.options || this.options.some(o => o === null || o === '')) {
        this.currentVariant = null;
        return;
      }
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => this.options[index] === option).includes(false);
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') return;
      if (!this.other.length) return;

      const fieldsets = this.other[0].querySelectorAll('fieldset');
      const list = Array.from(fieldsets);

      this.options.forEach((option, i) => {
        const fs = list[i];
        if (!fs) return;

        if (fs.querySelector('select')) {
          fs.querySelector('select').value = option || '';
        } else if (fs.querySelectorAll('input').length) {
          const radio = fs.querySelector(`input[value="${option}"]`);
          if (radio) radio.checked = true;
          else fs.querySelectorAll('input').forEach(r => (r.checked = false));
        }
      });

      // Keep the sibling picker in sync
      this.other[0].updateOptions();
      this.other[0].updateMasterId();
      this.other[0].updateVariantText();
      this.other[0].setDisabled();
      this.other[0].setImageSetMedia();
    }

    updateMedia() {
      if (!this.currentVariant?.featured_media || !this.productSlider) return;
      const mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      const activeMedia = this.productSlider.querySelector(`[data-media-id="${mediaId}"]`);

      this.productSlider.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
      this.setImageSetMedia();

      if (!activeMedia) return;
      activeMedia.classList.add('is-active');
      activeMedia.parentElement.prepend(activeMedia);

      window.setTimeout(() => {
        const header_h = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        window.scrollTo({ left: 0, top: (activeMedia.parentElement.offsetTop - header_h), behavior: 'instant' });
      });
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment`
      );
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;

        if (this.currentVariant?.id) {
          input.value = this.currentVariant.id;
          input.removeAttribute('disabled');
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          input.value = '';
          input.setAttribute('disabled', 'disabled');
        }
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');
      if (!pickUpAvailability) return;

      if (this.currentVariant?.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;
      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [
        `price-${this.dataset.section}`,
        `price-${this.dataset.section}--sticky`,
        `product-image-${this.dataset.section}--sticky`,
        `inventory-${this.dataset.section}`,
        `sku-${this.dataset.section}`,
        `quantity-${this.dataset.section}`,
        `stock-message-${this.dataset.section}`,
        `pre-order-${this.dataset.section}`,
        `bbis-${this.dataset.section}`, // NEW
      ];
    }


    renderProductInfo() {
  if (!this.currentVariant) return;

  const sections = this.getSectionsToRender();
  fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
    .then(r => r.text())
    .then(htmlText => {
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      let preOrder = false;
      let preOrderLimitReached = false;
      let bbisLimitReached = false; // NEW

      sections.forEach((id) => {
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(id);
        const price_fixed = document.getElementById(id + '--sticky');
        if (price) price.classList.remove('visibility-hidden');
        if (price_fixed) price_fixed.classList.remove('visibility-hidden');

        if (id.includes('pre-order') && source) {
          if (source.innerHTML !== '') preOrder = true;
          else if (source.className?.includes('product--pre-order--limit-reached')) preOrderLimitReached = true;
        }

        if (id.includes('bbis') && source) { // NEW
          if (source.className?.includes('product--bbis--limit-reached')) bbisLimitReached = true;
        }
      });

      // Priority: BBIS cap → hard stop (sold out), otherwise use existing logic.
      if (bbisLimitReached) {
        this.toggleAddButton(true, window.theme.variantStrings.soldOut, true, /*preOrder*/ false);
      } else if (!preOrderLimitReached) {
        this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut, true, preOrder);
      } else {
        this.toggleAddButton(true, window.theme.variantStrings.soldOut, true, preOrder);
      }
    });
}


       

toggleAddButton(disable = true, text = false, modifyClass = true, preOrder = false) {
  const productForm = document.getElementById(`product-form-${this.dataset.section}`);
  if (!productForm) return;

  const productTemplate = productForm.getAttribute('template');
  const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
  if (!submitButtons.length) return;

  // Helper: read per-variant JSON (price + discount)
  const getPreorderPriceData = () => {
    const idInput = productForm.querySelector('input[name="id"]');
    const variantId = idInput && idInput.value;
    if (!variantId) return null;

    const jsonEl = document.getElementById(`preorder-data-${variantId}`);
    if (!jsonEl) return null;

    let data = null;
    try { data = JSON.parse(jsonEl.textContent); }
    catch (e) { return null; }

    const discountRaw = Number(data.fixed_amount_off || 0);
    const hasDiscount = discountRaw > 0 && data.price;

    if (!hasDiscount) return null;

    return {
      price: data.price,                     // discounted price
      discount_money: data.fixed_amount_off_money || null
    };
  };

  const priceData = getPreorderPriceData();

  submitButtons.forEach((btn) => {
    const t = btn.querySelector('.single-add-to-cart-button--text');
    if (!t) return;

    const soldOutText   = text || btn.dataset?.soldOutText   || (window.theme?.variantStrings?.soldOut   || 'Sold out');
    const preorderText  = btn.dataset?.preorderText          || (window.theme?.variantStrings?.preOrder  || 'Pre-order');
    const addToCartText = btn.dataset?.addToCartText         || (window.theme?.variantStrings?.addToCart || 'Add to cart');

    const isPreorderTemplate = productTemplate?.includes('pre-order') || preOrder;

    if (disable) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('sold-out');
      t.textContent = soldOutText;
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('loading', 'sold-out');

      if (isPreorderTemplate && priceData) {
        // --- MAIN LINE (NO compare_at_price) ---
        const mainLine =
          `${preorderText} - ${priceData.price}`;

        // --- SUB LINE (KEEP SAVINGS) ---
        const subLine = priceData.discount_money
          ? `<span class="atc-sub-line">Forudbestil og spar ${priceData.discount_money}</span>`
          : '';

        t.innerHTML = `<span class="atc-main-line">${mainLine}</span>${subLine}`;

      } else if (isPreorderTemplate) {
        t.textContent = preorderText;
      } else {
        t.textContent = addToCartText;
      }
    }
  });

  if (!modifyClass) return;
}

    setUnavailable() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((btn) => {
        const t = btn.querySelector('.single-add-to-cart-button--text');
        if (!t) return;
        t.textContent = window.theme.variantStrings.unavailable;
        btn.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') return;
      const variant_data = this.getVariantData();
      if (!(variant_data && this.currentVariant)) return true;

      const selected_options = this.currentVariant.options.map((value, index) => ({ value, index: `option${index + 1}` }));
      const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

      this.fieldsets.forEach((fieldset, i) => {
        const fieldset_options = Object.values(available_options)[i];
        if (!fieldset_options) return;

        if (fieldset.querySelector('select')) {
          fieldset_options.forEach((option) => {
            const opt = fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']');
            if (opt) opt.disabled = !!option.isUnavailable;
          });
        } else if (fieldset.querySelectorAll('input').length) {
          fieldset.querySelectorAll('input').forEach((input, input_i) => {
            input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
          });
        }
      });

      return true;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;
      const dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        const ih = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]');
        this.imageSetIndex = ih?.dataset.index;
        this.setImageSetMedia();
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) return;
      const v = this.currentVariant?.[this.imageSetIndex];
      if (!v) return;

      const setValue = this.getImageSetName(v);
      const group = this.imageSetName + '_' + setValue;
      const selected_set_images = this.productSlider.querySelectorAll(`[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants) {
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      } else {
        Array.from(selected_set_images)
          .filter((el) => el.dataset.group === group)
          .forEach(thumb => thumb.parentElement.prepend(thumb));
      }
    }

    createAvailableOptionsTree(variant_data, selected_options) {
      return variant_data.reduce((options, variant) => {
        Object.keys(options).forEach(index => {
          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);
          if (typeof entry === 'undefined') {
            entry = { value: variant[index], isUnavailable: true };
            options[index].push(entry);
          }

          const countMatch = selected_options.reduce((count, { value, index: idx }) => {
            return variant[idx] === value ? count + 1 : count;
          }, 0);

          if (countMatch >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          if ((!this.currentVariant || !this.currentVariant.available) &&
              selected_options.find((o) => o.value === entry.value && index === o.index)) {
            entry.isUnavailable = true;
          }

          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, { option1: [], option2: [], option3: [] });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }

  customElements.define('variant-selects', VariantSelects);

  class VariantRadios extends VariantSelects {
    constructor() { super(); }
    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fs) => {
        const chosen = Array.from(fs.querySelectorAll('input')).find((radio) => radio.checked);
        return chosen ? chosen.value : null;
      });
    }
  }
  customElements.define('variant-radios', VariantRadios);
}

/* ---------------- Product slider ---------------- */
if (!customElements.get('product-slider')) {
  class ProductSlider extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-buttons');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');

      const observer = new MutationObserver(() => this.setupProductGallery());
      observer.observe(this, { attributes: true, attributeFilter: ['class'], childList: true });

      this.setupProductGallery();

      if (this.pagination) {
        this.setupPagination();
        this.addEventListener('scroll', this.updatePagination.bind(this));
        const resizeObserver = new ResizeObserver(() => this.onPaginationResize());
        resizeObserver.observe(this);
      }
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) return;
      this.setEventListeners();
    }
    buildItems(activeImages) {
      return activeImages.map((item) => {
        const link = item.querySelector('.product-single__media-zoom');
        return { src: link.getAttribute('href'), msrc: link.dataset.msrc, w: link.dataset.w, h: link.dataset.h, title: link.getAttribute('title') };
      });
    }
    setEventListeners() {
      const activeImages = Array.from(this.querySelectorAll('.product-images__slide--image')).filter(el => el.clientWidth > 0);
      const items = this.buildItems(activeImages);
      const captionEl = this.dataset.captions;
      const pswpElement = document.querySelectorAll('.pswp')[0];
      const options = {
        maxSpreadZoom: 2, loop: false, allowPanToNext: false, closeOnScroll: false, showHideOpacity: false,
        arrowKeys: true, history: false, captionEl: captionEl, fullscreenEl: false, zoomEl: false,
        shareEl: false, counterEl: true, arrowEl: true, preloaderEl: true
      };

      const openPswp = function (e, link, options, pswpElement, items) {
        const parent = link.closest('.product-images__slide');
        const i = activeImages.indexOf(parent);
        options.index = parseInt(i, 10);
        options.getThumbBoundsFn = () => {
          const thumbnail = link.closest('.product-single__media');
          const pageYScroll = window.scrollY || document.documentElement.scrollTop;
          const rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        };
        if (typeof PhotoSwipe !== 'undefined') {
          const pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
          pswp.listen('firstUpdate', () => pswp.listen('parseVerticalMargin', (item) => { item.vGap = { top: 50, bottom: 50 }; }));
          pswp.init();
        }
        e.preventDefault();
      };

      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        const thumbnail = link.closest('.product-single__media');
        const clone = link.cloneNode(true);
        thumbnail.append(clone);
        link.remove();
        clone.addEventListener('click', (e) => openPswp(e, clone, options, pswpElement, items));
      });
    }
    setupPagination() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(el => el.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.currentPageElement = this.pagination.querySelector('.slider-counter--current');
      this.pageTotalElement = this.pagination.querySelector('.slider-counter--total');
      this.prevButton = this.pagination.querySelector('button[name="previous"]');
      this.nextButton = this.pagination.querySelector('button[name="next"]');

      this.prevButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(el => el.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    }
    onPaginationButtonClick(event) {
      event.preventDefault();
      this.slideScrollPosition = event.currentTarget.name === 'next'
        ? this.scrollLeft + (1 * this.sliderItemOffset)
        : this.scrollLeft - (1 * this.sliderItemOffset);
      this.scrollTo({ left: this.slideScrollPosition });
    }
    updatePagination() {
      if (!this.nextButton) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] }
        }));
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.scrollLeft === 0) this.prevButton.setAttribute('disabled', 'disabled');
      else this.prevButton.removeAttribute('disabled');

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) this.nextButton.setAttribute('disabled', 'disabled');
      else this.nextButton.removeAttribute('disabled');
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/* ---------------- Product form (AJAX ATC) ---------------- */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.sticky) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;
      this.hideErrors = this.dataset.hideErrors === 'true';

      // Ensure correct variant id for products WITHOUT options (no picker on page)
      this.ensureIdForNoVariantPicker();
    }

    ensureIdForNoVariantPicker() {
      const scope = this.closest('.thb-product-detail') || document;
      const hasPicker = !!scope.querySelector('variant-selects, variant-radios, [data-variant-selects]');
      const idInput = this.form.querySelector('input[name="id"]');
      if (!idInput) return;

      if (!hasPicker) {
        // If value is empty, try to grab from installment form (your theme already prints it there)
        if (!idInput.value) {
          const inst = document.getElementById('product-form-installment');
          const instId = inst?.querySelector('input[name="id"]')?.value;
          if (instId) idInput.value = instId;
        }
        // Enable it
        idInput.removeAttribute('disabled');
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form.reportValidity()) return;

      const scope = this.closest('.thb-product-detail') || document;
      const picker =
        scope.querySelector('variant-selects') ||
        scope.querySelector('variant-radios') ||
        scope.querySelector('[data-variant-selects]');

      const idInput = this.form.querySelector('input[name="id"]');
      const hasValidVariant = idInput && !idInput.disabled && idInput.value && idInput.value !== '';

      // If there IS a picker: require a chosen variant
      if (picker && !hasValidVariant) {
        // Prefer inline error on the picker
        if (typeof picker.showInlineError === 'function') {
          picker.showInlineError('Vælg venligst en størrelse.');
        } else {
          this.handleErrorMessage('Vælg venligst en størrelse.');
        }

        const HEADER_OFFSET = 88; // adjust to your sticky header height
        const rect = picker.getBoundingClientRect();
        const top = rect.top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top, behavior: 'smooth' });
        picker.classList.add('variant-pulse');
        setTimeout(() => picker.classList.remove('variant-pulse'), 600);
        return;
      }

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      submitButtons.forEach((btn) => {
        if (btn.classList.contains('loading')) return;
        btn.setAttribute('aria-disabled', true);
        btn.classList.add('loading');
      });

      this.handleErrorMessage();

      const config = {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
      };

      const formData = new FormData(this.form);
      const giftProduct = formData.get('gift');

      if (giftProduct != null) {
        const giftProducts = this.form.querySelector('.gift-products');
        const giftProductsError = giftProducts.querySelector('.product-form__error-message-wrapper');

        if (!giftProductsError.hasAttribute('hidden')) giftProductsError.setAttribute('hidden', '');

        if (giftProduct == '0') {
          giftProductsError.removeAttribute('hidden');
          submitButtons.forEach((btn) => {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
          });
          return;
        }

        const giftFormData = {
          items: [
            { id: giftProduct, quantity: 1 },
            { id: formData.get('id'), quantity: formData.get('quantity') }
          ],
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: window.location.pathname,
        };

        const result = fetch(`${theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
          body: JSON.stringify(giftFormData),
        })
        .then((r) => r.json())
        .catch((e) => { console.error(e); });

        result.then((response) => {
          if (!response) return;

          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => { console.error(e); })
        .finally(() => {
          submitButtons.forEach((btn) => {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
          });
        });

      } else {
        formData.append('sections', this.getSectionsToRender().map((s) => s.section));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              dispatchCustomEvent('product:variant-error', {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);
              return;
            }

            this.renderContents(response);
            dispatchCustomEvent('cart:item-added', {
              product: response.hasOwnProperty('items') ? response.items[0] : response
            });
          })
          .catch((e) => { console.error(e); })
          .finally(() => {
            submitButtons.forEach((btn) => {
              btn.classList.remove('loading');
              btn.removeAttribute('aria-disabled');
            });
          });
      }
    }

    getSectionsToRender() {
      return [
        { id: 'Cart',               section: 'main-cart',   selector: '.thb-cart-form' },
        { id: 'Cart-Drawer',        section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) return;
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (typeof CartDrawer !== 'undefined') new CartDrawer();
        if (typeof Cart !== 'undefined') new Cart().renderContents(parsedState);
      }));

      const product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.querySelector('.product-quick-images--container').classList.remove('active');
        document.body.classList.remove('open-quick-view');

        if (window.innerWidth < 1069) {
          product_drawer.classList.remove('active');
          if (document.getElementById('Cart-Drawer')) {
            document.getElementById('Cart-Drawer').classList.add('active');
            document.body.classList.add('open-cart');
            document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full').classList.add('active');
            dispatchCustomEvent('cart-drawer:open');
          }
        } else {
          product_drawer.querySelector('.product-quick-images--container').addEventListener('transitionend', function () {
            product_drawer.classList.remove('active');
            if (document.getElementById('Cart-Drawer')) {
              document.getElementById('Cart-Drawer').classList.add('active');
              document.body.classList.add('open-cart');
              document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full').classList.add('active');
              dispatchCustomEvent('cart-drawer:open');
            }
          });
        }

        if (!document.getElementById('Cart-Drawer')) {
          document.body.classList.remove('open-cc');
        }
      } else if (document.getElementById('Cart-Drawer')) {
        document.body.classList.add('open-cc', 'open-cart');
        document.getElementById('Cart-Drawer').classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }
  });
}

/* ---------------- Sticky ATC, Side Panel, Swiper, Countdown, Stories ---------------- */
/* (unchanged from previous rewrite; included for completeness) */

if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() { super(); this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined'; }
    connectedCallback() { this.setupObservers(); this.setupToggle(); }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner');
      const content = this.querySelector('.product-add-to-cart-sticky--content');
      if (this.animations_enabled) {
        const tl = gsap.timeline({ reversed: true, paused: true, onStart: () => { button.classList.add('sticky-open'); }, onReverseComplete: () => { button.classList.remove('sticky-open'); } });
        tl.set(content, { display: 'block', height: 'auto' }, 'start').from(content, { height: 0, duration: 0.25 }, 'start+=0.001');
        button.addEventListener('click', function () { tl.reversed() ? tl.play() : tl.reverse(); return false; });
      } else {
        button.addEventListener('click', function () { content.classList.toggle('active'); return false; });
      }
    }
    setupObservers() {
      const _this = this;
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      const footer = document.getElementById('footer');
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach((entry) => {
          if (entry.target === footer) {
            if (entry.intersectionRatio > 0) { /* */ }
            else if (entry.intersectionRatio === 0 && _this.formPassed) { _this.classList.add('sticky--visible'); }
          }
          if (entry.target === form) {
            const rect = form.getBoundingClientRect();
            if (entry.intersectionRatio === 0 && window.scrollY > (rect.top + rect.height)) { _this.formPassed = true; _this.classList.add('sticky--visible'); }
            else if (entry.intersectionRatio === 1) { _this.formPassed = false; _this.classList.remove('sticky--visible'); }
          }
        });
      }, { threshold: [0, 1] });
      _this.formPassed = false;
      if (form) observer.observe(form);
      if (footer) observer.observe(footer);
    }
  }
  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (!customElements.get('side-panel-links')) {
  class ProductSidePanelLinks extends HTMLElement {
    constructor() { super(); this.links = this.querySelectorAll('button'); this.drawer = document.getElementById('Product-Information-Drawer'); this.buttons = this.drawer?.querySelector('.side-panel-content--tabs'); this.panels = this.drawer?.querySelector('.side-panel-content--inner')?.querySelectorAll('.side-panel-content--tab-panel'); this.body = document.body; }
    connectedCallback() { this.links.forEach((btn, i) => { btn.addEventListener('click', () => { this.body.classList.add('open-cc'); this.buttons?.toggleActiveClass?.(i); this.drawer?.classList.add('active'); }); }); }
  }
  customElements.define('side-panel-links', ProductSidePanelLinks);
}

if (typeof addIdToRecentlyViewed !== "undefined") { addIdToRecentlyViewed(); }

window.addEventListener('load', function() {
  const swiperProductImages = document.querySelector('.swiper-product-images');

  if (swiperProductImages) {
    const productThumbnails = new Swiper('.swiper-product-thumbnails', {
      slidesPerView: 'auto',
      spaceBetween: 10,
      freeMode: true,
      mousewheel: { forceToAxis: true }
    });

    const productImages = new Swiper('.swiper-product-images', {
      // core: 1 → 2&3 → 3&4 ...
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      autoHeight: true,
      thumbs: { swiper: productThumbnails },

      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },

      // No fade – we want a real horizontal slider with two visible on desktop
      effect: 'slide',
      allowTouchMove: true,

      // Breakpoints: 1 per view on mobile, 2 per view on tablet/desktop
      breakpoints: {
        768: {
          slidesPerView: 2,
          slidesPerGroup: 1,   // next click = 2&3, not 3&4
          spaceBetween: 16,
          autoHeight: false    // more stable when 2 images have different heights
        }
      }
    });

    // Keep your video play/pause logic – still works with slide effect
    productImages.on('transitionEnd', function() {
      const prev   = swiperProductImages.querySelector('.swiper-slide-prev video');
      const active = swiperProductImages.querySelector('.swiper-slide-active video');
      const next   = swiperProductImages.querySelector('.swiper-slide-next video');

      if (prev) prev.pause();
      if (next) next.pause();
      if (active) active.play();
    });
  }

  /* everything below stays EXACTLY as you have it now */
  const giftProducts = document.querySelectorAll('.gift-product');
  if (giftProducts.length > 0) {
    giftProducts.forEach((giftProduct) => {
      const giftProductId = giftProduct.getAttribute('for');
      const giftProductCheckbox = document.querySelector('.gift-products input[id="' + giftProductId + '"]');
      const giftProductSize = giftProduct.querySelector('select');
      if (giftProductSize && giftProductCheckbox) {
        giftProductSize.addEventListener('change', (e) => { giftProductCheckbox.value = e.target.value; });
      }
    });
  }

  const videoBlockPosters = document.querySelectorAll('.product--videos-block--poster');
  if (videoBlockPosters.length > 0) {
    videoBlockPosters.forEach((poster) => {
      poster.addEventListener('click', (e) => {
        const el = e.currentTarget;
        const wrap = el.closest('.product--videos-block');
        const video = wrap.querySelector('video');
        document.querySelectorAll('video').forEach((v) => v.pause());
        el.remove();
        if (video) video.play();
      });
    });
  }
});

/* Shipping countdown (unchanged) */
function updateStockStatus() {
  const timerElement = document.getElementById('timer');
  const shippingTimeElement = document.getElementById('shipping-time');
  const orderBeforeElement = document.getElementById('order-before');
  if (!(timerElement && shippingTimeElement && orderBeforeElement)) return;

  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();

  if (day === 0) {
    shippingTimeElement.innerText = "Bestil i dag og vi sender i morgen!";
    orderBeforeElement.style.display = "none";
    timerElement.innerText = "";
  } else if ((day === 5 && hours >= 13) || day === 6) {
    shippingTimeElement.innerText = "Bestil i dag, så sender vi på mandag";
    orderBeforeElement.style.display = "none";
    timerElement.innerText = "";
  } else {
    const next14 = new Date();
    if (hours >= 14) next14.setDate(now.getDate() + 1);
    next14.setHours(14, 0, 0, 0);

    if (next14.getDay() === 6) next14.setDate(next14.getDate() + 2);
    else if (next14.getDay() === 0) next14.setDate(next14.getDate() + 1);

    const diff = next14 - now;
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((diff % (1000 * 60)) / 1000);

    timerElement.innerText = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    shippingTimeElement.innerText = hours < 14 ? "I dag." : "I morgen.";
    orderBeforeElement.style.display = "inline";
  }
}
function startCountdown() { setInterval(updateStockStatus, 1000); }
document.addEventListener('DOMContentLoaded', startCountdown);

/* Stories overlay boot (unchanged) */
(function() {
  function initContainer(container) {
    if (!container || container.dataset.storiesInit === '1') return;
    container.dataset.storiesInit = '1';

    const overlayLocal = container.querySelector('.story-overlay');
    const videoLocal   = container.querySelector('.story-overlay__video');
    if (!overlayLocal || !videoLocal) return;

    let overlay = overlayLocal;
    let video   = videoLocal;
    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
      overlay.classList.add('story-overlay--global');
    }

    const closeButtons = overlay.querySelectorAll('[data-close]');
    const backdrop     = overlay.querySelector('.story-overlay__backdrop');

    if (overlay.dataset.storiesOverlayInit !== '1') {
      overlay.dataset.storiesOverlayInit = '1';

      overlay.addEventListener('click', function(e) {
        if (e.target === backdrop || e.target.hasAttribute('data-close')) {
          e.preventDefault();
          closeOverlay();
        }
      });

      document.addEventListener('keydown', function(e) {
        if (!overlay.hidden && (e.key === 'Escape' || e.key === 'Esc')) {
          e.preventDefault();
          closeOverlay();
        }
      });
    }

    function hardResetVideo() {
      try { video.pause(); } catch(e){}
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.muted = false;
      video.load();
      video.style.display = 'none';
    }

    function openOverlay(src, poster) {
      hardResetVideo();
      if (poster) video.setAttribute('poster', poster);
      video.setAttribute('src', src);
      video.style.display = 'block';

      overlay.hidden = false;
      document.body.style.overflow = 'hidden';

      const tryPlay = () => video.play().catch(() => {
        video.muted = true;
        return video.play().catch(()=>{});
      });

      video.load();
      if (video.readyState >= 2) tryPlay();
      else video.addEventListener('canplay', tryPlay, { once: true });
    }

    function closeOverlay() {
      hardResetVideo();
      overlay.hidden = true;
      document.body.style.overflow = '';
    }

    container.addEventListener('click', function(e) {
      const story = e.target.closest('.js-story');
      if (!story || !container.contains(story)) return;
      e.preventDefault();
      e.stopPropagation();
      const src = story.getAttribute('data-src');
      if (!src) return;
      openOverlay(src, story.getAttribute('data-poster') || '');
    });

    container.addEventListener('keydown', function(e) {
      const story = e.target.closest('.js-story');
      if (story && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        const src = story.getAttribute('data-src');
        if (!src) return;
        openOverlay(src, story.getAttribute('data-poster') || '');
      }
    });
  }

  function bootAll() { document.querySelectorAll('[id^="stories-"]').forEach(initContainer); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  document.addEventListener('shopify:section:load', function(e) {
    e.target && e.target.querySelectorAll && e.target
      .querySelectorAll('[id^="stories-"]')
      .forEach(initContainer);
  });
})();

/* Optional CSS to add in your theme for nicer inline error:
.variant-error {
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fee;
  border: 1px solid #f5c2c7;
  font-size: 14px;
  line-height: 1.3;
  color: #842029;
}
.variant-pulse { animation: variantPulse 0.6s ease; }
@keyframes variantPulse {
  0%   { box-shadow: 0 0 0 0 rgba(230, 0, 0, 0.35); }
  100% { box-shadow: 0 0 0 10px rgba(230, 0, 0, 0); }
}
*/
